import { ReactNode, useEffect, useRef, useState } from 'react';
import { useUserInfo } from '@domain/user/useUserInfo';
import { PostViewOptions } from '../../constants/constants';
import { Post } from '../../types/Post';
import { PostDetailsView } from '../PostDetailsView/PostDetailsView';
import { PostLikesView } from '../PostLikesView/PostLikesView';
import { PostCommentsView } from './PostCommentsView/PostCommentsView';
import { PostsListView } from './PostsListView/PostsListView';
import { RegisterWallView } from '../RegisterWallView/RegisterWallView';
import { getDisplayUserDetails } from '../../helpers/getDisplayUserDetails';

interface PostsViewerTouchDeviceProps {
  posts: Post[];
  currentPostId: string;
  hasMore: boolean;
  loadMore: () => void;
  onClose: (catchPostId?: string) => void;
  setCurrentPostId: (catchId: string) => Promise<void>;
  showMoreTeaser?: ReactNode;
  inMapDrawer?: boolean;
}

/**
 * Component for viewing a list of posts on touch devices.
 *
 * @component
 * @param {Post[]} posts - Array of posts to be displayed.
 * @param {string} currentPostId - ID of the currently selected post.
 * @param {boolean} hasMore - Flag indicating if there are more posts to load.
 * @param {() => void} loadMore - Function to load more posts.
 * @param {(catchPostId?: string) => void} onClose - Function to handle closing the viewer.
 * @param {(catchId: string) => Promise<void>} setCurrentPostId - Function to set the current post ID.
 * @param {ReactNode} [showMoreTeaser] - Optional teaser element to show more posts.
 * @param {boolean} [inMapDrawer=false] - Flag indicating if the viewer is in a map drawer (different layout).
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @remarks
 * This component should only be used with a list of posts (> 1) on touch devices.
 * It has logic for scrolling to the correct post when switching between views.
 *
 */
export const PostsViewerTouchDevice = ({
  posts,
  currentPostId,
  hasMore,
  loadMore,
  onClose: handleClose,
  setCurrentPostId,
  showMoreTeaser = null,
  inMapDrawer = false,
}: PostsViewerTouchDeviceProps) => {
  const [selectedView, setSelectedView] = useState<PostViewOptions>('postList');
  const desiredViewName = useRef<PostViewOptions>('postList');
  const { isLoggedIn } = useUserInfo();
  const currentPost = posts.find(({ _id }) => _id === currentPostId);
  const hasCatch = !!currentPost?.catch;
  const userId = currentPost?.user._id;
  const userAvatarUrl = currentPost?.user.avatar?.url;
  const userNickname = currentPost?.user.nickname;

  const displayEntity = currentPost?.displayEntity;
  const displayUserDetails = getDisplayUserDetails(
    displayEntity,
    userId ?? '',
    userNickname ?? '',
    userAvatarUrl ?? '',
  );

  const scrollSelector = selectedView === 'postDetailsWithScroll' ? '#post-products' : undefined;
  const isPostCommentsView = selectedView === 'postComments';
  const isPostDetailsView = selectedView.includes('postDetails');
  const isPostListView = selectedView === 'postList';
  const isPostLikesView = selectedView === 'postLikes' && currentPostId;
  const isPostAuthorDataAvailable =
    !!displayUserDetails.displayUserNickname && !!displayUserDetails.displayUserAvatarUrl;
  const isRegisterWallView = selectedView === 'registerWallView' && isPostAuthorDataAvailable;

  useEffect(() => {
    if (isLoggedIn) {
      setSelectedView(desiredViewName.current);
    }
  }, [isLoggedIn]);

  const handleBackButtonClick = (): void => {
    setSelectedView('postList');
  };

  const handleViewChange = (viewName: PostViewOptions, viewPostId?: string): void => {
    if (!isLoggedIn) {
      desiredViewName.current = viewName;
      setSelectedView('registerWallView');
      return;
    }
    if (viewPostId) {
      void setCurrentPostId(viewPostId);
    }
    setSelectedView(viewName);
  };

  return (
    <div
      className={inMapDrawer ? '' : 'z-modal fixed top-0 left-0 h-dvh w-screen'}
      data-testid="PostsViewerTouchDevice"
    >
      {isPostCommentsView && (
        <PostCommentsView
          posts={posts}
          currentPostId={currentPostId}
          className="col-start-4 col-end-6 row-start-1 rounded-r-lg"
          onBackButtonClick={handleBackButtonClick}
          onViewChange={handleViewChange}
        />
      )}
      {isPostDetailsView && (
        <PostDetailsView
          currentPostId={currentPostId}
          className="col-start-4 col-end-6 row-start-1 rounded-r-lg"
          onBackButtonClick={handleBackButtonClick}
          scrollSelector={scrollSelector}
          hasCatch={hasCatch}
        />
      )}
      {isPostListView && (
        <PostsListView
          posts={posts}
          currentPostId={currentPostId}
          hasMore={hasMore}
          loadMore={loadMore}
          onBackButtonClick={handleClose}
          onViewChange={handleViewChange}
          showMoreTeaser={showMoreTeaser}
        />
      )}
      {isPostLikesView && (
        <PostLikesView currentPostId={currentPostId} onBackButtonClick={handleBackButtonClick} />
      )}
      {isRegisterWallView && (
        <RegisterWallView
          onBackButtonClick={handleBackButtonClick}
          userAvatarUrl={displayUserDetails.displayUserAvatarUrl}
          userNickname={displayUserDetails.displayUserNickname}
        />
      )}
    </div>
  );
};
